import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Star, Instagram, Send } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import ProductCard from "../components/ProductCard";
import { TESTIMONIALS, GALLERY } from "../data/products";
import { fetchProducts, fetchCategories, absUrl } from "../lib/storefront";
import { useReveal } from "../hooks/useReveal";
import { useContent } from "../lib/content";

export default function Home() {
  const [email, setEmail] = useState("");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const { content } = useContent();
  const home = content?.home || {};
  const heroRef = useReveal();
  const featRef = useReveal();
  const catRef = useReveal();
  const bestRef = useReveal();
  const testiRef = useReveal();
  const galRef = useReveal();
  const newsRef = useReveal();

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => setProducts([]));
    fetchCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  const newArrivals = products.filter((p) => p.badge === "New").slice(0, 4);
  const bestSellers = products
    .filter((p) => p.badge === "Best Seller" || (p.rating || 0) >= 4.8)
    .slice(0, 4);

  const heroMain = absUrl(home.hero_image_main) || "/hero/1.jpg";
  const heroSec = absUrl(home.hero_image_secondary) || "/hero/2.jpg";
  const heroTer = absUrl(home.hero_image_tertiary) || "/hero/3.jpg";

  const handleNewsletter = (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    toast.success("Welcome to the Flownté circle ♡", {
      description: "Your first-look invitation is on its way.",
    });
    setEmail("");
  };

  return (
    <div data-testid="home-page">
      {/* HERO — refreshed, mobile-first, CMS-driven */}
      <section
        ref={heroRef}
        className="reveal relative overflow-hidden bg-gradient-to-b from-blush-50 via-cream-50 to-cream-100"
      >
        <div className="blob bg-blush-200 w-[420px] h-[420px] -top-32 -left-16 opacity-60" />
        <div className="blob bg-blush-100 w-[380px] h-[380px] top-32 -right-16 opacity-50" />

        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 md:px-12 pt-6 md:pt-20 pb-14 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10 lg:gap-12 items-center">
          {/* TEXT COLUMN */}
          <div className="lg:col-span-6 order-2 lg:order-1 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/85 backdrop-blur border border-blush-100 shadow-sm">
              <Sparkles size={14} strokeWidth={1.5} className="text-blush-600" />
              <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-blush-600 font-body">
                {home.hero_eyebrow}
              </span>
            </div>

            <h1 className="mt-4 sm:mt-6 font-heading text-[2.4rem] sm:text-5xl lg:text-7xl leading-[1.05] text-ink-900 tracking-tight">
              {home.hero_title_lead}
              <br />
              <span className="font-script text-5xl sm:text-6xl lg:text-8xl text-blush-600 italic">
                {home.hero_title_accent}
              </span>{" "}
              {home.hero_title_tail}
            </h1>

            <p className="mt-4 sm:mt-6 text-sm sm:text-lg font-body text-ink-700 leading-relaxed max-w-xl mx-auto lg:mx-0">
              {home.hero_paragraph}
            </p>

            <div className="mt-6 sm:mt-10 flex flex-wrap items-center justify-center lg:justify-start gap-3">
              <Link
                to="/shop"
                onClick={() => {
                  try { window.localStorage.setItem("flonte_pending_coupon_v1", "WELCOME10"); } catch { /* ignore */ }
                }}
                data-testid="hero-shop-btn"
                className="btn-maroon inline-flex items-center gap-2"
              >
                {home.hero_cta_primary || "Shop the collection"}
                <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
              <Link
                to="/about"
                data-testid="hero-about-btn"
                className="btn-ghost-rose"
              >
                {home.hero_cta_secondary || "Our story"}
              </Link>
            </div>

            <div className="mt-8 sm:mt-12 flex items-center justify-center lg:justify-start gap-5 sm:gap-6 text-ink-700 font-body">
              <Stat value={home.stat_1_value} label={home.stat_1_label} />
              <span className="w-px h-9 sm:h-10 bg-blush-200" />
              <Stat value={home.stat_2_value} label={home.stat_2_label} />
              <span className="w-px h-9 sm:h-10 bg-blush-200" />
              <Stat value={home.stat_3_value} label={home.stat_3_label} />
            </div>
          </div>

          {/* IMAGE COLUMN */}
          <div className="lg:col-span-6 order-1 lg:order-2 relative">
            {/* Mobile: elegant text-only hero with coupon + urgency chips (no images) */}
            <div className="md:hidden relative mx-auto max-w-[460px]">
              <div className="relative rounded-[1.75rem] overflow-hidden bg-gradient-to-br from-blush-100 via-cream-50 to-blush-200 p-6 shadow-blush-lg border border-blush-200">
                <div className="absolute -top-12 -right-10 h-40 w-40 rounded-full bg-blush-300/40 blur-3xl" />
                <div className="absolute -bottom-14 -left-10 h-44 w-44 rounded-full bg-blush-200/40 blur-3xl" />

                <div className="relative space-y-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/90 border border-blush-200">
                    <Sparkles size={12} strokeWidth={1.5} className="text-blush-600" />
                    <span className="text-[10px] uppercase tracking-[0.22em] text-blush-600 font-body font-medium">
                      Limited atelier drop
                    </span>
                  </div>

                  <div className="bg-white/85 backdrop-blur rounded-2xl border border-blush-200 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-blush-600 font-body">
                          First order
                        </p>
                        <p className="font-heading text-2xl text-ink-900 leading-tight mt-1">
                          Flat 10% off
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          try {
                            navigator.clipboard.writeText("WELCOME10");
                            window.localStorage.setItem("flonte_pending_coupon_v1", "WELCOME10");
                            toast.success("WELCOME10 copied", { description: "Auto-applied at checkout" });
                          } catch { /* ignore */ }
                        }}
                        data-testid="hero-coupon-copy-btn"
                        className="px-3 py-1.5 rounded-full bg-blush-600 text-white font-body tracking-[0.22em] text-[11px] hover:bg-blush-700 transition shrink-0"
                      >
                        Copy · WELCOME10
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <Chip icon="✓" label="Free shipping" />
                    <Chip icon="₹" label="COD available" />
                    <Chip icon="↺" label="7-day returns" />
                  </div>

                  <div className="flex items-center gap-2 text-[11px] font-body text-ink-700 bg-white/70 rounded-xl px-3 py-2 border border-blush-100">
                    <span className="h-2 w-2 rounded-full bg-blush-600 animate-pulse" />
                    <span>
                      <span className="font-medium">48 new pieces</span> just arrived · dispatch within 24h
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tablet & desktop: editorial 2-image stack with floating card */}
            <div className="hidden md:grid relative grid-cols-12 gap-4 h-[560px] lg:h-[640px]">
              <div className="col-span-7 row-span-2 rounded-[2rem] overflow-hidden shadow-blush-lg bg-cream-100 relative">
                <img src={heroMain} alt="Flownté hero" className="absolute inset-0 h-full w-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-900/15 via-transparent to-transparent" />
              </div>
              <div className="col-span-5 flex flex-col gap-4">
                <div className="flex-1 rounded-[2rem] overflow-hidden shadow-blush bg-cream-100 relative">
                  <img src={heroSec} alt="" className="absolute inset-0 h-full w-full object-cover" />
                </div>
                <div className="flex-1 rounded-[2rem] overflow-hidden shadow-blush bg-cream-100 relative">
                  <img src={heroTer} alt="" className="absolute inset-0 h-full w-full object-cover" />
                </div>
              </div>

              <div className="absolute -bottom-6 -left-4 bg-white rounded-2xl shadow-blush-lg border border-blush-100 p-4 flex items-center gap-3 animate-float-slow">
                <div className="h-11 w-11 rounded-full bg-blush-100 grid place-items-center text-blush-600">
                  <Sparkles size={16} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-heading text-base text-ink-900 leading-tight">
                    {home.hero_badge_title}
                  </p>
                  <p className="text-xs font-body text-ink-500">{home.hero_badge_sub}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED — New Arrivals */}
      <section
        ref={featRef}
        className="reveal relative py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div>
              <span className="font-script text-3xl text-blush-400">just arrived</span>
              <h2 className="font-heading text-4xl md:text-5xl text-ink-900 mt-1">
                New Arrivals
              </h2>
              <p className="mt-3 font-body text-ink-700 max-w-xl">
                Soft-structured pieces woven this season. Wear them to brunches, weddings, or
                quiet Sunday afternoons.
              </p>
            </div>
            <Link
              to="/shop"
              data-testid="featured-viewall-btn"
              className="btn-ghost-rose inline-flex items-center gap-2"
            >
              View all <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 stagger">
            {newArrivals.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section
        ref={catRef}
        className="reveal relative py-20 md:py-28 bg-gradient-to-b from-cream-100 via-blush-50/60 to-cream-100"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto">
            <span className="font-script text-3xl text-blush-400">explore</span>
            <h2 className="font-heading text-4xl md:text-5xl text-ink-900 mt-1">
              Collections for every mood
            </h2>
            <p className="mt-3 font-body text-ink-700">
              From laid-back linens to wedding-ready velvets, find the silhouette that moves with
              you.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 stagger">
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/shop?cat=${c.id}`}
                data-testid={`category-card-${c.id}`}
                className="group relative overflow-hidden rounded-[2rem] aspect-[4/5] shadow-sm hover:shadow-blush-lg transition-shadow duration-500"
              >
                <img
                  src={c.image}
                  alt={c.name}
                  className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blush-700/55 via-blush-600/10 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 text-white">
                  <span className="font-script text-2xl text-blush-100">
                    {c.caption.split(" ")[0]}
                  </span>
                  <h3 className="font-heading text-3xl mt-1 text-white">{c.name}</h3>
                  <p className="mt-2 text-sm font-body text-white/85">{c.caption}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] font-body">
                    Shop now <ArrowRight size={14} strokeWidth={1.5} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BEST SELLERS */}
      <section ref={bestRef} className="reveal relative py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div>
              <span className="font-script text-3xl text-blush-400">loved by many</span>
              <h2 className="font-heading text-4xl md:text-5xl text-ink-900 mt-1">
                Best Sellers
              </h2>
              <p className="mt-3 font-body text-ink-700 max-w-xl">
                The pieces that find their way back into carts, week after week.
              </p>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 stagger">
            {bestSellers.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section
        ref={testiRef}
        className="reveal relative py-20 md:py-28 bg-gradient-to-b from-cream-100 via-blush-50/80 to-cream-100"
      >
        <div className="blob bg-blush-200 w-[380px] h-[380px] -top-20 left-0" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto">
            <span className="font-script text-3xl text-blush-400">love notes</span>
            <h2 className="font-heading text-4xl md:text-5xl text-ink-900 mt-1">
              From women like you
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 stagger">
            {TESTIMONIALS.map((t) => (
              <figure
                key={t.id}
                data-testid={`testimonial-${t.id}`}
                className="bg-white rounded-3xl p-8 border border-blush-100 shadow-sm hover:shadow-blush transition-shadow duration-500"
              >
                <div className="flex items-center gap-1 text-blush-600">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} size={14} strokeWidth={1.5} fill="currentColor" />
                  ))}
                </div>
                <blockquote className="mt-5 font-heading text-xl text-ink-900 leading-relaxed italic">
                  “{t.quote}”
                </blockquote>
                <figcaption className="mt-6 flex items-center gap-3">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="h-10 w-10 rounded-full object-cover ring-1 ring-blush-200"
                  />
                  <div>
                    <p className="font-body text-sm text-ink-900">{t.name}</p>
                    <p className="font-body text-xs text-ink-500">{t.city}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* INSTAGRAM GALLERY */}
      <section ref={galRef} className="reveal relative py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto">
            <span className="font-script text-3xl text-blush-400">follow along</span>
            <h2 className="font-heading text-4xl md:text-5xl text-ink-900 mt-1 flex items-center justify-center gap-3">
              <Instagram size={28} strokeWidth={1.3} className="text-blush-600" />
              @flownte.in
            </h2>
            <p className="mt-3 font-body text-ink-700">
              A daily mood-board of petal tones, behind-the-loom moments, and Flownté women.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {GALLERY.map((src, i) => (
              <a
                key={i}
                href="https://instagram.com/flownte.in"
                target="_blank"
                rel="noreferrer"
                data-testid={`gallery-item-${i}`}
                className="group relative overflow-hidden rounded-2xl aspect-square"
              >
                <img
                  src={src}
                  alt=""
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-blush-600/0 group-hover:bg-blush-600/30 transition-colors grid place-items-center">
                  <Instagram
                    size={22}
                    strokeWidth={1.4}
                    className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section ref={newsRef} className="reveal relative py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="relative rounded-[2.5rem] bg-gradient-to-br from-blush-100 via-blush-50 to-cream-50 border border-blush-100 p-10 md:p-16 text-center overflow-hidden">
            <div className="blob bg-blush-200 w-[280px] h-[280px] -top-20 -right-10 opacity-60" />
            <span className="font-script text-3xl text-blush-600 relative">be first</span>
            <h3 className="mt-2 font-heading text-4xl md:text-5xl text-ink-900 relative">
              Early access, gently delivered.
            </h3>
            <p className="mt-4 font-body text-ink-700 max-w-lg mx-auto relative">
              Join the Flownté circle for new-drop previews, private sales, and the occasional
              love letter from our atelier.
            </p>

            <form
              onSubmit={handleNewsletter}
              className="mt-8 flex flex-col sm:flex-row items-stretch gap-3 max-w-lg mx-auto relative"
              data-testid="newsletter-form"
            >
              <input
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email"
                className="field flex-1"
                required
              />
              <button
                type="submit"
                data-testid="newsletter-submit"
                className="btn-maroon inline-flex items-center justify-center gap-2"
              >
                <Send size={15} strokeWidth={1.5} /> Join the circle
              </button>
            </form>
            <p className="mt-4 text-[11px] font-body text-ink-500 uppercase tracking-[0.22em] relative">
              No spam, only softness.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <p className="font-heading text-xl sm:text-2xl text-ink-900">{value}</p>
      <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-ink-500">{label}</p>
    </div>
  );
}

function Chip({ icon, label }) {
  return (
    <div className="flex flex-col items-center justify-center gap-0.5 rounded-xl bg-white/85 backdrop-blur border border-blush-200 px-2 py-2 text-center">
      <span className="text-blush-600 font-heading text-base leading-none">{icon}</span>
      <span className="text-[9px] uppercase tracking-[0.14em] text-ink-700 font-body leading-tight">{label}</span>
    </div>
  );
}

